import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useOllie from 'src/hooks/useOllie'
import useOllieFindings from 'src/hooks/useOllieFindings'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import {
  OLLIE_CONTEXTUAL_ASK_LABEL,
  resolveOllieContextualAsk,
} from 'src/utils/ollieContextualAsk'
import {
  trackOllieContextualAskOpened,
  trackOllieDiscoverabilityExposed,
} from 'src/utils/ollieDiscoverability'
import {
  ollieNoticedChecksFor,
  ollieNoticedTakesAnyCheck,
} from 'src/utils/ollieNoticedRows'
import OllieAskLink from './OllieAskLink'

/**
 * Page-level "Ask Ollie about this". Uses the live finding for this workflow
 * when one exists (checkId + served askOllie question); otherwise a verified
 * how-to fallback. Hides when neither is available so unsupported asks are
 * never advertised. Gated by discoverability rollout for staged measurement.
 */
const OllieContextualAsk = ({
  page = null,
  fallback = null,
  label = OLLIE_CONTEXTUAL_ASK_LABEL,
  quiet = true,
  className,
  'data-testid': testId = 'ollie-contextual-ask',
}) => {
  const { enabled } = useOllie()
  const { venueId } = useOllieVenueId()
  const discoverability = useSelector(
    state => state.ollie?.discoverabilityEntries,
  )
  const cohort = discoverability?.cohort || 'all'
  const entriesEnabled = discoverability?.enabled !== false
  const checkIds = page ? ollieNoticedChecksFor(page) : []
  const anyCheck = page ? ollieNoticedTakesAnyCheck(page) : false
  const { findings, forChecks } = useOllieFindings({
    autoLoad: Boolean(page) && checkIds.length > 0 && entriesEnabled,
    checkIds: page ? (anyCheck ? null : checkIds) : [],
  })

  const resolved = useMemo(
    () =>
      entriesEnabled
        ? resolveOllieContextualAsk({
            page,
            findings: page ? (anyCheck ? findings : forChecks) : [],
            fallback,
            label,
          })
        : null,
    [entriesEnabled, page, anyCheck, findings, forChecks, fallback, label],
  )

  useEffect(() => {
    if (!resolved || !venueId) return
    trackOllieDiscoverabilityExposed({
      venueId,
      entryPoint: resolved.entryPoint,
      page,
      cohort,
    })
  }, [resolved, venueId, page, cohort])

  if (!enabled || !resolved) return null

  return (
    <OllieAskLink
      className={className}
      data-testid={testId}
      quiet={quiet}
      checkId={resolved.checkId || undefined}
      question={resolved.question}
      label={resolved.label}
      onOpen={() =>
        trackOllieContextualAskOpened({
          venueId,
          entryPoint: resolved.entryPoint,
          page,
          checkId: resolved.checkId,
          question: resolved.question,
          cohort,
        })
      }
    />
  )
}

export default OllieContextualAsk
